import React, { useRef } from "react";
import { css, Theme, useTheme } from "@emotion/react";

import window from "core/includes/window";
import Cart from "core/Components/Icons/Cart";
import { useAppState } from "core/contexts/AppContext";
import CartItemCount from "core/Components/Navigations/CartItemCount";
import FloatingNotification from "core/Components/Floating/FloatingNotification";

type Props = {
    closeBurgerMenu: ({forcedClose}: any) => void,
    isDisabledCartIcon?: boolean,
}

const CartIcon: React.FC<Props> = ({ closeBurgerMenu, isDisabledCartIcon = false }) => {
    const hasClickedCart = window?.sessionStorage?.getItem("has-clicked-cart") === "true";
    const [appState, setAppState] = useAppState();
    const theme: Theme = useTheme();
    const itemCount = appState.cart?.products?.length || 0;

    const onClickHandler = () => {
        window?.sessionStorage?.setItem("has-clicked-cart", "true");
        if (!isDisabledCartIcon) {
            setAppState({
                isOpenCartPopup: true,
                cartMessage: null,
            });
            closeBurgerMenu({ forcedClose: true });
        }
    };

    const cartRef = useRef<HTMLDivElement | null>(null);

    const jiggleDurationMs = 500;
    const timeBetweenJigglesMs = 30_000;
    const jiggleEndPercent = jiggleDurationMs / timeBetweenJigglesMs * 100;

    const style = css`
        height: 35px;
        width: 35px;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;

        :hover {
            background-color: ${theme.colours.onahau};
        }

        ${!!itemCount && css`
            background-color: ${theme.colours.curiousBlue};

            svg > path {
                fill: white;
            }

            ${!hasClickedCart && css`
                @keyframes jiggle {
                    0% {
                        transform: scale(1.1, 0.9) rotate(3deg);
                    }
                    ${jiggleEndPercent * 1 / 3} % {
                        transform: scale(0.9, 1.1) translate(0, -5px);
                    } ${jiggleEndPercent * 2 / 3}% {
                        transform: scale(1.0, 1.0) translate(0, -5px) rotate(-3deg);
                    } ${jiggleEndPercent}% {
                        transform: scale(1.0, 1.0) rotate(0deg);
                    }

                    100% {
                        transform: scale(1.0, 1.0) rotate(0deg);
                    }
                } 
            
                animation: jiggle ${timeBetweenJigglesMs}ms infinite ease;
            `};
        `};

        > div {
            ${appState.isOpenCartPopup && css` pointer-events: none; `};
        }

        ${!isDisabledCartIcon && css`
            cursor: pointer;

            &:hover {
                > div > .cart-icon {
                    > svg > path {
                        fill: ${theme.colours.scienceBlue};
                    }
                }
            }
        `}
        
        ${theme.breakpoints.up("xxl")} {
            margin-right: 20px;
        }

        ${theme.breakpoints.between("lg", "xl")} {
            margin-right: 10px;
        }

        ${theme.breakpoints.down("md")} {
            margin-right: ${theme.sizes.menu.sideMargin}px;
        }
    `;

    const cartStyle = css`
        width: 25px;
        height: 25px;
        z-index: 1;
    `;

    const clickableDivStyle = css`
        position: relative;
        display: flex;
        align-items: center;
        height: 100%;
    `;

    return (
        <>
            <div ref={cartRef} css={style} data-test-id={"cart-icon-container"}>
                <div onClick={onClickHandler} css={clickableDivStyle}>
                    <Cart className={'cart-icon'} css={cartStyle}/>
                    {!isDisabledCartIcon && (
                        <CartItemCount itemCount={itemCount}/>
                    )}
                </div>
            </div>
            {!!appState.cartMessage && !!cartRef.current &&(
                <FloatingNotification
                    referenceEl={cartRef.current}
                    placement="bottom"
                    message={appState.cartMessage}
                    disableAutoDismiss={sessionStorage.getItem("has-clicked-cart") !== "true"}
                    onClose={() => {
                        setAppState({
                            cartMessage: null,
                        });
                    }}
                />
            )}
        </>
    );
};

export default CartIcon;